import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Button from "../../components/ui/button/Button";
import TableDropdown from "../../components/common/TableDropdown";
import Badge from "../../components/ui/badge/Badge";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";
import { fetchWellsHistory, WellHistory, fetchWellFilings, Filing, fetchWellComponents, WellComponent, normalizeApi14 } from "../../lib/api/wells";
import { listManualWBDs } from "../../lib/api/manual-wbd";
import type { ManualWBDRecord } from "../../lib/api/manual-wbd";
import { listResearchSessions } from "../../lib/api/research";
import type { ResearchSession } from "../../lib/api/research";
import DocumentUploader from "../../components/documents/DocumentUploader"
import { listWellDocuments, deleteDocument } from "../../lib/api/documents"
import type { ExtractedDocumentSummary } from "../../lib/api/documents";
import { apiGetBlob } from "../../lib/api-client";

function friendlyResearchError(raw: string): string {
  if (raw.includes('could not be attributed') || raw.includes('no_match'))
    return 'No public records found for this well from the state agency.'
  if (raw.includes('available_apis') || raw.includes('other well'))
    return 'Found a matching lease, but the records belong to different wells.'
  if (raw.includes('triage'))
    return 'State agency document lookup did not find a match for this well.'
  return 'Research pipeline encountered an error.'
}

const VALID_TABS = ["Public Well Data", "Documents", "Filings", "Diagrams", "Research"] as const;

export default function WellDetail() {
  const { api14 } = useParams<{ api14: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [well, setWell] = useState<WellHistory | null>(null);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Honor ?tab= URL param on initial render; fall back to default if absent/invalid
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t && (VALID_TABS as readonly string[]).includes(t) ? t : "Public Well Data";
  })();
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [components, setComponents] = useState<WellComponent[]>([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentsFetched, setComponentsFetched] = useState(false);
  const [diagrams, setDiagrams] = useState<ManualWBDRecord[]>([]);
  const [diagramsLoading, setDiagramsLoading] = useState(false);
  const [diagramsFetched, setDiagramsFetched] = useState(false);
  const [researchSessions, setResearchSessions] = useState<ResearchSession[]>([]);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchFetched, setResearchFetched] = useState(false);
  const [researchDocs, setResearchDocs] = useState<ExtractedDocumentSummary[]>([]);
  const [researchDocsLoading, setResearchDocsLoading] = useState(false);
  const [researchDocsFetched, setResearchDocsFetched] = useState(false);
  const [tenantDocs, setTenantDocs] = useState<ExtractedDocumentSummary[]>([]);
  const [tenantDocsLoading, setTenantDocsLoading] = useState(false);
  const [tenantDocsFetched, setTenantDocsFetched] = useState(false);
  const [viewingDocId, setViewingDocId] = useState<number | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const [docDeleteDeleting, setDocDeleteDeleting] = useState(false);
  const [docDeleteError, setDocDeleteError] = useState<string | null>(null);
  const deleteDocModal = useModal();
  const itemsPerPage = 25;

  useEffect(() => {
    const loadWellDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!api14) {
          setError("API number is required");
          setLoading(false);
          return;
        }

        // Fetch well data
        const response = await fetchWellsHistory();
        // Normalize both sides: the route param is canonical 14-digit (10-digit
        // padded with trailing "0000"), but a well row may be stored bare 10-digit
        // — a strict `===` misses that match. See card #138.
        const foundWell = response.wells.find(
          (w) => normalizeApi14(w.api14) === normalizeApi14(api14)
        );

        if (!foundWell) {
          setError(`Well with API ${api14} not found`);
          setLoading(false);
          return;
        }

        setWell(foundWell);

        // Fetch filings/interactions from unified endpoint
        try {
          const filingsResponse = await fetchWellFilings(api14, {
            page: 1,
            page_size: itemsPerPage,
            ordering: "-updated_at",
          });
          setFilings(filingsResponse.filings || []);
        } catch (filingsErr) {
          console.warn("[WellDetail] Error loading filings:", filingsErr);
          // Don't fail the page if filings fail to load
          setFilings([]);
        }
      } catch (err) {
        console.error("[WellDetail] Error loading well:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load well details"
        );
      } finally {
        setLoading(false);
      }
    };

    loadWellDetail();
  }, [api14]);

  useEffect(() => {
    if (activeTab === "Documents" && !componentsFetched && api14) {
      const loadComponents = async () => {
        try {
          setComponentsLoading(true);
          const data = await fetchWellComponents(api14);
          setComponents(data);
        } catch (err) {
          console.warn("[WellDetail] Error loading components:", err);
          setComponents([]);
        } finally {
          setComponentsLoading(false);
          setComponentsFetched(true);
        }
      };
      loadComponents();
    }
  }, [activeTab, componentsFetched, api14]);

  useEffect(() => {
    if (activeTab === "Diagrams" && !diagramsFetched && api14) {
      const loadDiagrams = async () => {
        try {
          setDiagramsLoading(true);
          const data = await listManualWBDs({ api14 });
          setDiagrams(data.results);
        } catch (err) {
          console.warn("[WellDetail] Error loading diagrams:", err);
          setDiagrams([]);
        } finally {
          setDiagramsLoading(false);
          setDiagramsFetched(true);
        }
      };
      loadDiagrams();
    }
  }, [activeTab, diagramsFetched, api14]);

  useEffect(() => {
    if (activeTab === "Research" && !researchFetched && api14) {
      const loadResearch = async () => {
        try {
          setResearchLoading(true);
          const sessions = await listResearchSessions({ well_api14: api14 });
          setResearchSessions(sessions);
        } catch (err) {
          console.warn("[WellDetail] Error loading research sessions:", err);
          setResearchSessions([]);
        } finally {
          setResearchLoading(false);
          setResearchFetched(true);
        }
      };
      loadResearch();
    }
  }, [activeTab, researchFetched, api14]);

  useEffect(() => {
    if (activeTab === 'Documents' && !researchDocsFetched && api14) {
      setResearchDocsLoading(true)
      listWellDocuments(api14, 'research')
        .then(r => { setResearchDocs(r.documents); setResearchDocsFetched(true) })
        .catch(() => setResearchDocsFetched(true))
        .finally(() => setResearchDocsLoading(false))
    }
  }, [activeTab, researchDocsFetched, api14]);

  useEffect(() => {
    if (activeTab === 'Documents' && !tenantDocsFetched && api14) {
      setTenantDocsLoading(true)
      listWellDocuments(api14, 'tenant')
        .then(r => { setTenantDocs(r.documents); setTenantDocsFetched(true) })
        .catch(() => setTenantDocsFetched(true))
        .finally(() => setTenantDocsLoading(false))
    }
  }, [activeTab, tenantDocsFetched, api14]);

  const getFilingRoute = (filing: Filing): string => {
    const meta = filing.metadata as { plan_id?: string; session_id?: string };
    switch (filing.form_type) {
      // W-3A and C-103 are both PlanSnapshots — navigate using plan_id (e.g. "4230132998:combined")
      case "W-3A":
      case "C-103":
        return meta.plan_id ? `/plan/${meta.plan_id}` : `/filings`;
      // W-3 and Sundry are both W3FormORM records linked to a wizard session
      case "W-3":
      case "Sundry":
        return meta.session_id ? `/w3-wizard/${meta.session_id}` : `/filings`;
      default:
        return `/filings`;
    }
  };

  const getStatusColor = (status: string): "success" | "warning" | "error" | "primary" => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("approved") || statusLower.includes("completed")) {
      return "success";
    }
    if (statusLower.includes("pending") || statusLower.includes("processing")) {
      return "warning";
    }
    if (statusLower.includes("rejected") || statusLower.includes("failed")) {
      return "error";
    }
    return "primary";
  };

  const formatRelativeTime = (iso: string): string => {
    try {
      const now = Date.now();
      const then = new Date(iso).getTime();
      const diffMs = now - then;
      const diffSeconds = Math.floor(diffMs / 1000);
      if (diffSeconds < 60) return "just now";
      const diffMinutes = Math.floor(diffSeconds / 60);
      if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes !== 1 ? "s" : ""} ago`;
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `${diffHours} hr${diffHours !== 1 ? "s" : ""} ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
      const diffMonths = Math.floor(diffDays / 30);
      return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
    } catch {
      return iso;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const viewDocument = async (docId: number): Promise<void> => {
    setViewingDocId(docId);
    try {
      const blob = await apiGetBlob(`/api/documents/${docId}/pdf/`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to open document.");
    } finally {
      setViewingDocId(null);
    }
  };

  const openDeleteDocModal = (docId: number) => {
    setDeletingDocId(docId);
    setDocDeleteError(null);
    deleteDocModal.openModal();
  };

  const handleDeleteDocConfirm = async () => {
    if (deletingDocId === null) return;
    setDocDeleteDeleting(true);
    setDocDeleteError(null);
    try {
      await deleteDocument(deletingDocId);
      setTenantDocs((prev) => prev.filter((d) => d.id !== deletingDocId));
      deleteDocModal.closeModal();
    } catch (err) {
      setDocDeleteError(err instanceof Error ? err.message : "Failed to delete document.");
    } finally {
      setDocDeleteDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="Well Details" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              Loading well details...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (error || !well) {
    return (
      <>
        <PageBreadcrumb pageTitle="Well Details" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error || "Well not found"}
              </p>
              <Button
                onClick={() => navigate("/well-registry")}
                className="mt-4"
                variant="outline"
              >
                Back to Well Registry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const tenantComponents = components.filter((c) => c.layer === "tenant");

  return (
    <>
      <PageMeta
        title={`${well.lease_name} ${well.well_number} | RegulAgent`}
        description="View well details and filing history"
      />

      <PageBreadcrumb pageTitle={`${well.lease_name} #${well.well_number}`} />

      <button
        onClick={() => navigate("/well-registry")}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors mb-2"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Wells
      </button>

      <div className="space-y-6">
        {/* Tab Navigation + Content Card */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          {/* Tab Header */}
          <div className="border-b border-gray-200 px-6 dark:border-white/[0.05] flex items-center justify-between">
            <div className="flex gap-1">
              {["Public Well Data", "Documents", "Filings", "Diagrams", "Research"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? "border-brand-500 text-brand-600 dark:text-brand-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            {activeTab === "Filings" && (
              <Button
                onClick={() => navigate("/filings/new", { state: { api14, state: well.state } })}
                size="sm"
              >
                Create Filing
              </Button>
            )}
            {activeTab === "Diagrams" && (
              <Button
                onClick={() => navigate(`/manual-wbd/new?api14=${api14}`)}
                size="sm"
              >
                Create New Diagram
              </Button>
            )}
            {activeTab === "Research" && (
              <Button
                onClick={() => navigate(`/research?api14=${api14}`)}
                size="sm"
              >
                Start Research
              </Button>
            )}
          </div>

          {/* Public Well Data Tab */}
          {activeTab === "Public Well Data" && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { label: "API Number", value: well.api14 },
                  { label: "Operator", value: well.operator_name },
                  { label: "Lease Name", value: well.lease_name },
                  { label: "Well Number", value: well.well_number },
                  { label: "Field", value: well.field_name },
                  { label: "County", value: well.county },
                  { label: "State", value: well.state },
                  {
                    label: "Last Interaction",
                    value: well.tenant_interaction?.last_interaction_at
                      ? new Date(well.tenant_interaction.last_interaction_at).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : "—",
                  },
                  {
                    label: "Last Interaction Type",
                    value: well.tenant_interaction?.last_interaction_type || "—",
                  },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-800 dark:text-white/90">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents Tab — private uploads + research pipeline docs */}
          {activeTab === "Documents" && (
            <div className="p-6 space-y-8">

              {/* Privacy note */}
              <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] px-4 py-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Files you upload here are private to your account and are not shared with other users or visible in the public well registry.
                </p>
              </div>

              {/* Upload Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Upload Your Documents</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Uploaded files are processed and searchable only by you.
                </p>
                <DocumentUploader
                  apiNumber={api14 ?? ""}
                  onSuccess={() => {
                    setTenantDocsFetched(false)
                  }}
                />
              </div>

              {/* Tenant Uploaded Docs List */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Your Uploaded Documents</h3>
                {tenantDocsLoading ? (
                  <div className="flex items-center gap-2 py-4">
                    <div className="h-4 w-4 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                    <span className="text-sm text-gray-500">Loading…</span>
                  </div>
                ) : tenantDocs.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 py-4">No documents uploaded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-white/[0.05]">
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Type</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">File</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Status</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Uploaded</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantDocs.map(doc => (
                          <tr key={doc.id} className="border-b border-gray-100 dark:border-white/[0.05] hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200 uppercase text-xs">{doc.document_type ?? '—'}</td>
                            <td className="py-2 px-3 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{doc.file_name ?? '—'}</td>
                            <td className="py-2 px-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                doc.status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                doc.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>{doc.status}</span>
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-xs">{new Date(doc.created_at).toLocaleDateString()}</td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => viewDocument(doc.id)}
                                  disabled={viewingDocId === doc.id}
                                  className="text-xs font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {viewingDocId === doc.id ? "Loading…" : "View"}
                                </button>
                                <button
                                  onClick={() => openDeleteDocModal(doc.id)}
                                  className="text-xs font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Well Components (tenant layer) */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Well Components</h3>
                {componentsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
                    <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading components...</span>
                  </div>
                ) : tenantComponents.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No custom components added</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                        <TableRow>
                          <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            Component Name
                          </TableCell>
                          <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            Type
                          </TableCell>
                          <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            Layer
                          </TableCell>
                          <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            Depth From
                          </TableCell>
                          <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            Depth To
                          </TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {tenantComponents.map((component) => (
                          <TableRow key={component.id}>
                            <TableCell className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-300">
                              {component.component_name}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                              {component.component_type}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                              {component.layer}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                              {component.depth_from != null ? `${component.depth_from} ft` : "—"}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                              {component.depth_to != null ? `${component.depth_to} ft` : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Research Pipeline Documents */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Research Pipeline Documents</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Files indexed from public records via the research pipeline (state agencies).
                </p>
                {researchDocsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                    <span className="ml-3 text-sm text-gray-500">Loading documents…</span>
                  </div>
                ) : researchDocs.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 py-4">No research documents found. Start a research session to populate this list.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-white/[0.05]">
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Type</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">File</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Source</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Status</th>
                          <th className="py-2 px-3 text-xs font-medium text-gray-500">Indexed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {researchDocs.map(doc => (
                          <tr key={doc.id} className="border-b border-gray-100 dark:border-white/[0.05] hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200 uppercase text-xs">{doc.document_type ?? '—'}</td>
                            <td className="py-2 px-3 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{doc.file_name ?? '—'}</td>
                            <td className="py-2 px-3">
                              <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {doc.source_type}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                doc.status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                doc.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>{doc.status}</span>
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-xs">{new Date(doc.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* Diagrams Tab */}
          {activeTab === "Diagrams" && (
            <div className="p-6 space-y-6">
              <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] px-4 py-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Files you upload here are private to your account and are not shared with other users or visible in the public well registry.
                </p>
              </div>
              {diagramsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
                  <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading diagrams...</span>
                </div>
              ) : diagrams.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                    No diagrams saved for this well yet
                  </p>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/manual-wbd/new?api14=${api14}`)}
                  >
                    Create First Diagram
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                      <TableRow>
                        <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                          Title
                        </TableCell>
                        <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                          Type
                        </TableCell>
                        <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                          Updated
                        </TableCell>
                        <TableCell isHeader className="py-3 px-4 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                          <span className="sr-only">Actions</span>
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                      {diagrams.map((diagram) => (
                        <TableRow key={diagram.id}>
                          <TableCell className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-300">
                            {diagram.title || <span className="italic text-gray-400">Untitled</span>}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              diagram.diagram_type === "current"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                : diagram.diagram_type === "planned"
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            }`}>
                              {diagram.diagram_type === "current"
                                ? "Current"
                                : diagram.diagram_type === "planned"
                                ? "Planned"
                                : "As-Plugged"}
                            </span>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatRelativeTime(diagram.updated_at)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-400">
                            <button
                              onClick={() => navigate(`/manual-wbd/${diagram.id}`)}
                              className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium transition-colors"
                            >
                              Edit
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Research Tab */}
          {activeTab === "Research" && (
            <div className="p-6 space-y-6">
              <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] px-4 py-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Files you upload here are private to your account and are not shared with other users or visible in the public well registry.
                </p>
              </div>
              {researchLoading ? (
                <div className="text-center py-8">
                  <span className="text-gray-400 dark:text-gray-500">Loading research sessions...</span>
                </div>
              ) : researchSessions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 mb-4">No research sessions found for this well.</p>
                  <Button size="sm" onClick={() => navigate(`/research?api14=${api14}`)}>
                    Start Research
                  </Button>
                </div>
              ) : (
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3">Documents</th>
                      <th className="py-2 px-3">Created</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {researchSessions.map((sess) => (
                      <React.Fragment key={sess.id}>
                        <tr className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-2 px-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              sess.status === 'ready' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              sess.status === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {sess.status}
                            </span>
                          </td>
                          <td className="py-2 px-3">{sess.indexed_documents}/{sess.total_documents}</td>
                          <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{new Date(sess.created_at).toLocaleDateString()}</td>
                          <td className="py-2 px-3">
                            <button
                              onClick={() => navigate(`/research/${sess.id}`)}
                              className="text-brand-500 hover:text-brand-600 text-sm font-medium"
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                        {sess.status === 'error' && (
                          <tr className="border-b dark:border-gray-700">
                            <td colSpan={4} className="px-3 pb-3">
                              <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                                <p className="font-medium text-amber-800">
                                  {friendlyResearchError(sess.error_message ?? '')}
                                </p>
                                <p className="mt-1 text-amber-700">
                                  You can upload your own documents (W-2, completion report, C-103)
                                  — they'll be processed privately, for your account only.
                                </p>
                                <button
                                  onClick={() => setActiveTab('Documents')}
                                  className="mt-2 text-sm font-medium text-blue-600 underline"
                                >
                                  Upload Documents →
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Filings Tab */}
          {activeTab === "Filings" && (
            <>
              <div className="px-6 pt-4 pb-0">
                <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] px-4 py-3 mb-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Files you upload here are private to your account and are not shared with other users or visible in the public well registry.
                  </p>
                </div>
              </div>
              <div className="px-6 py-3 border-b border-gray-100 dark:border-white/[0.05]">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total: {filings.length} filings
                </p>
              </div>
              {filings.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No filings for this well yet
                  </p>
                  <Button onClick={() => navigate("/filings/new", { state: { api14, state: well.state } })}>
                    Create First Filing
                  </Button>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <div className="max-w-full overflow-x-auto">
                    <Table>
                      <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                        <TableRow>
                          <TableCell
                            isHeader
                            className="py-3 px-6 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                          >
                            Form
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                          >
                            Status
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                          >
                            Created On
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                          >
                            Last Updated
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                          >
                            <span className="sr-only">Actions</span>
                          </TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {filings.map((filing) => (
                          <TableRow key={filing.id}>
                            <TableCell className="px-6 py-4">
                              <span className="font-medium text-gray-800 text-theme-sm dark:text-gray-300">
                                {filing.form_type}
                              </span>
                            </TableCell>
                            <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                              <Badge
                                size="sm"
                                color={getStatusColor(filing.status)}
                              >
                                {filing.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-6 py-4 text-gray-700 whitespace-nowrap text-theme-sm dark:text-gray-400">
                              {formatDate(filing.created_at)}
                            </TableCell>
                            <TableCell className="px-6 py-4 text-gray-700 whitespace-nowrap text-theme-sm dark:text-gray-400">
                              {formatDate(filing.updated_at)}
                            </TableCell>
                            <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                              <div className="relative inline-block">
                                <TableDropdown
                                  dropdownButton={
                                    <button className="text-gray-500 dark:text-gray-400">
                                      <svg
                                        className="fill-current"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M5.99902 10.245C6.96552 10.245 7.74902 11.0285 7.74902 11.995V12.005C7.74902 12.9715 6.96552 13.755 5.99902 13.755C5.03253 13.755 4.24902 12.9715 4.24902 12.005V11.995C4.24902 11.0285 5.03253 10.245 5.99902 10.245ZM17.999 10.245C18.9655 10.245 19.749 11.0285 19.749 11.995V12.005C19.749 12.9715 18.9655 13.755 17.999 13.755C17.0325 13.755 16.249 12.9715 16.249 12.005V11.995C16.249 11.0285 17.0325 10.245 17.999 10.245ZM13.749 11.995C13.749 11.0285 12.9655 10.245 11.999 10.245C11.0325 10.245 10.249 11.0285 10.249 11.995V12.005C10.249 12.9715 11.0325 13.755 11.999 13.755C12.9655 13.755 13.749 12.9715 13.749 12.005V11.995Z"
                                          fill=""
                                        />
                                      </svg>
                                    </button>
                                  }
                                  dropdownContent={
                                    <>
                                      <button
                                        onClick={() => navigate(getFilingRoute(filing))}
                                        className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                                      >
                                        View Filing
                                      </button>
                                    </>
                                  }
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Back Button */}
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 dark:border-white/[0.05] dark:bg-white/[0.03]">
          <Button
            variant="outline"
            onClick={() => navigate("/well-registry")}
          >
            Back to Well Registry
          </Button>
        </div>
      </div>

      {/* Delete Document Confirmation Modal */}
      <Modal
        isOpen={deleteDocModal.isOpen}
        onClose={deleteDocModal.closeModal}
        className="max-w-md mx-4"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
              <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Delete Document?
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Are you sure you want to permanently delete this document? This action cannot be undone.
          </p>
          {docDeleteError && (
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">{docDeleteError}</p>
          )}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={deleteDocModal.closeModal}
              disabled={docDeleteDeleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDeleteDocConfirm}
              disabled={docDeleteDeleting}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              {docDeleteDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
